import React from 'react';
import { Star, Settings, Users, Heart, Archive, BarChart2, CheckCircle, Dice1 } from 'lucide-react';
import type { Game } from '../types';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';

interface GameStatsProps {
  game: Game;
}

export const GameStats: React.FC<GameStatsProps> = ({ game }) => {
  const { theme } = useAppTheme();
  // Usar diretamente as estatísticas passadas via props
  const gameStats = game.stats;

  // Usar as estatísticas diretas do jogo (average_rating e average_complexity podem vir da tabela principal ou das stats)
  // Dar prioridade aos valores calculados em stats se existirem, caso contrário, usar os da tabela games.
  const averageRating = Number(gameStats?.average_rating ?? game.average_rating ?? 0);
  const averageComplexity = Number(gameStats?.average_complexity ?? game.average_complexity ?? 0);

  // Garantir que as propriedades do objeto stats sejam números
  const ownedCount = Number(gameStats?.owned_count || 0);
  const wishlistCount = Number(gameStats?.wishlist_count || 0);
  const ratingCount = Number(gameStats?.rating_count || 0);
  const playCount = Number(gameStats?.play_count || 0);
  const playedCount = Number(gameStats?.played_count || 0);

  // Componente para exibir as estatísticas em badges, similar ao da página de ranking
  const GameStatsBadges = () => {
    if (!gameStats) {
      // Mensagem mais informativa caso stats não existam no objeto game
      console.log(`Objeto game.stats não encontrado para o jogo ${game.name}`);
      return (
        <div className={`px-3 py-1 rounded text-sm my-3 ${theme === 'dark' ? 'bg-yellow-700/30 text-yellow-300' : 'bg-yellow-50 text-yellow-700'}`}>
          Estatísticas da comunidade ainda não disponíveis.
        </div>
      );
    }
    
    // Adicionar verificação se algum stat é maior que zero para renderizar o container
    const hasStatsToShow = ownedCount > 0 || wishlistCount > 0 || playedCount > 0 || playCount > 0 || ratingCount > 0 || averageRating > 0 || averageComplexity > 0;

    if (!hasStatsToShow) {
        return (
            <div className={`text-sm italic my-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Ainda não há estatísticas da comunidade para este jogo.
            </div>
        );
    }

    return (
      <div className="flex flex-wrap gap-2 mt-4 mb-3">
        {ownedCount > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-indigo-700/50 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
            <CheckCircle className="w-4 h-4 mr-1" />
            <span>{ownedCount} {ownedCount === 1 ? 'pessoa possui' : 'pessoas possuem'}</span>
          </div>
        )}
        
        {wishlistCount > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-pink-700/50 text-pink-300' : 'bg-pink-50 text-pink-700'}`}>
            <Heart className="w-4 h-4 mr-1" />
            <span>{wishlistCount} na wishlist</span>
          </div>
        )}
        
        {playedCount > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-teal-700/50 text-teal-300' : 'bg-teal-50 text-teal-700'}`}>
            <Dice1 className="w-4 h-4 mr-1" />
            <span>{playedCount} {playedCount === 1 ? 'pessoa jogou' : 'pessoas jogaram'}</span>
          </div>
        )}
        
        {playCount > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-green-700/50 text-green-300' : 'bg-green-50 text-green-700'}`}>
            <Dice1 className="w-4 h-4 mr-1" />
            <span>{playCount} {playCount === 1 ? 'jogada' : 'jogadas'} no total</span>
          </div>
        )}
        
        {ratingCount > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-yellow-700/50 text-yellow-300' : 'bg-yellow-50 text-yellow-700'}`}>
            <Star className="w-4 h-4 mr-1" />
            <span>{ratingCount} {ratingCount === 1 ? 'avaliação' : 'avaliações'}</span>
          </div>
        )}

        {averageRating > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-yellow-700/50 text-yellow-300' : 'bg-yellow-50 text-yellow-700'}`}>
            <Star className="w-4 h-4 mr-1" />
            <span>Média {averageRating.toFixed(1)}</span>
          </div>
        )}

        {averageComplexity > 0 && (
          <div className={`px-3 py-1 rounded flex items-center text-sm ${theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
            <Settings className="w-4 h-4 mr-1" />
            <span>Complexidade {averageComplexity.toFixed(1)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className={`mt-8 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>Estatísticas da Comunidade</h2>
      </div>

      {/* Badges de estatísticas no estilo da página de ranking */}
      <GameStatsBadges />
    </section>
  );
}; 